/*
 * Shared chrome for mesh-* apps. Selectors are namespaced with `mesh-` so
 * they don't collide with app-specific class names.
 *
 * Each app's accent is set via --mesh-accent (defaulted by MeshShell).
 */

/* Third-party CSS imports must precede all other rules (PostCSS) */
@import "react-day-picker/style.css";

:root {
  --mesh-accent: #3aa8a1;
  --mesh-fg: #f1f5f9;
  --mesh-bg: #10151d;
  --mesh-muted: #9aa7b9;
  --mesh-surface: #171d26;
}

/* Theme providers supply semantic tokens; keep the original mesh variables as
 * aliases so existing apps opt into them without a visual rewrite. */
.mesh-theme,
[data-mesh-theme] {
  --mesh-bg: var(--mesh-canvas);
  --mesh-fg: var(--mesh-text);
  --mesh-muted: var(--mesh-text-muted);
  color: var(--mesh-text);
  font-family: var(--mesh-font-sans);
}

.mesh-app-root {
  --mesh-shell-top-clearance: max(
    4.5rem,
    calc(env(safe-area-inset-top) + 3.75rem)
  );
  position: relative;
  height: 100%;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow-x: clip;
  box-sizing: border-box;
  isolation: isolate;
  /* Inset content uses a top margin for the app bar clearance. Establish a
   * block formatting context so that margin stays inside the shell rather
   * than collapsing through the root and leaving a dead strip above it. */
  display: flow-root;
}

/* Shell controls are intentionally in the normal page geometry. This avoids
 * the old floating-control collision with an app's own hero, canvas, or
 * topbar while preserving a full-height child area below the utility bar. */
.mesh-app-content {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
}

/* An app opts into inset when it is a document-style or dashboard-like first
 * view. Legacy apps omit shellLayout until their full-viewport canvas or
 * topbar has a deliberate visual migration. */
.mesh-app-root[data-mesh-shell-layout="inset"] .mesh-app-content {
  height: calc(100% - var(--mesh-shell-top-clearance));
  margin-top: var(--mesh-shell-top-clearance);
}

/* The compatibility theme wrapper sits between #root and the legacy shell.
 * Preserve the percentage-height chain used by fullscreen/canvas apps. */
.mesh-shell-theme {
  width: 100%;
  height: 100%;
  min-height: 100%;
  min-width: 0;
  max-width: 100%;
  overflow-x: clip;
}

.mesh-settings-fab {
  position: absolute;
  top: max(env(safe-area-inset-top), 1rem);
  right: 1rem;
  width: 44px;
  height: 44px;
  font-size: 1.3rem;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.15);
  cursor: pointer;
  backdrop-filter: blur(8px);
  z-index: 30;
}

.mesh-invite-fab {
  position: absolute;
  top: max(env(safe-area-inset-top), 1rem);
  right: calc(1rem + 44px + 0.5rem);
  width: 44px;
  height: 44px;
  font-size: 1.2rem;
  border-radius: 50%;
  background: var(--mesh-accent);
  color: #0b0a06;
  border: 1px solid rgba(0, 0, 0, 0.25);
  cursor: pointer;
  backdrop-filter: blur(8px);
  z-index: 30;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

.mesh-invite-fab:hover {
  opacity: 0.85;
}

.mesh-invite-qr {
  display: flex;
  justify-content: center;
  padding: 0.5rem;
  background: #fff;
  border-radius: 0.5rem;
}

.mesh-invite-url {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.72rem;
  padding: 0.5rem 0.7rem;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 0.4rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  word-break: break-all;
  cursor: pointer;
  user-select: all;
}

.mesh-invite-url:hover {
  border-color: var(--mesh-accent);
}

.mesh-invite-actions {
  display: flex;
  gap: 0.6rem;
  align-items: center;
}

.mesh-invite-share {
  flex: 1;
  padding: 0.7rem 1rem;
  background: var(--mesh-accent);
  color: #0b0a06;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.95rem;
}

.mesh-invite-share:hover {
  opacity: 0.9;
}

.mesh-invite-copied {
  color: var(--mesh-accent);
  font-size: 0.85rem;
  font-weight: 600;
}

.mesh-invite-extras {
  padding: 0.6rem 0.7rem;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 0.4rem;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.mesh-invite-chain {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  font-size: 0.8rem;
  color: var(--mesh-muted);
}

.mesh-invite-chain strong {
  color: var(--mesh-fg);
  font-variant-numeric: tabular-nums;
}

.mesh-invite-room {
  margin: 0;
  font-size: 0.72rem;
  color: var(--mesh-muted);
  text-align: center;
}

.mesh-invite-room code {
  background: rgba(0, 0, 0, 0.35);
  padding: 0.1rem 0.35rem;
  border-radius: 0.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.mesh-self-ref {
  position: absolute;
  bottom: max(env(safe-area-inset-bottom), 0.5rem);
  right: 1rem;
  display: flex;
  gap: 0.4rem;
  font-size: 0.72rem;
  color: var(--mesh-muted);
  font-variant-numeric: tabular-nums;
  align-items: center;
  z-index: 40;
  pointer-events: auto;
}

.mesh-self-ref a {
  color: var(--mesh-accent);
  text-decoration: none;
}

.mesh-self-ref a:hover {
  opacity: 0.7;
}

/* ------------------------------------------------------------------------- */
/* Visual foundation — deliberate product chrome, not floating demo controls. */
/* ------------------------------------------------------------------------- */

.mesh-app-bar {
  position: absolute;
  top: max(env(safe-area-inset-top), 0.75rem);
  right: max(env(safe-area-inset-right), 0.75rem);
  left: max(env(safe-area-inset-left), 0.75rem);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  z-index: 60;
  pointer-events: none;
}

.mesh-app-bar-brand,
.mesh-app-bar-actions {
  min-width: 0;
  pointer-events: auto;
}

/* Legacy canvas stages own their first-screen composition. Keep the product
 * name in their own title unless the app explicitly opts into a location trail. */
.mesh-app-root[data-mesh-shell-layout="overlay"]:not(
    [data-mesh-breadcrumbs="enabled"]
  )
  .mesh-app-bar-brand {
  display: none;
}

.mesh-app-bar-brand {
  display: inline-flex;
  min-height: 2.5rem;
  max-width: min(28rem, calc(100vw - 12rem));
  align-items: center;
  gap: 0.5rem;
  padding: 0.42rem 0.68rem;
  overflow: hidden;
  border: 1px solid var(--mesh-border, rgb(255 255 255 / 16%));
  border-radius: var(--mesh-radius-lg, 1rem);
  background: var(--mesh-surface);
  background: color-mix(in srgb, var(--mesh-surface) 87%, transparent);
  box-shadow: 0 0.65rem 1.7rem rgb(0 0 0 / 19%);
  backdrop-filter: blur(16px);
  color: var(--mesh-fg);
  white-space: nowrap;
}

.mesh-app-bar-eyebrow {
  color: var(--mesh-muted);
  font-size: 0.67rem;
  font-weight: 740;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.mesh-app-bar-title {
  overflow: hidden;
  color: var(--mesh-fg);
  font-size: 0.82rem;
  font-weight: 720;
  letter-spacing: -0.012em;
  text-overflow: ellipsis;
}

.mesh-app-bar-breadcrumbs {
  min-width: 0;
  max-width: 100%;
}

.mesh-breadcrumbs {
  min-width: 0;
  color: var(--mesh-muted);
}

.mesh-breadcrumbs-list {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 0.35rem;
  margin: 0;
  padding: 0;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  list-style: none;
  white-space: nowrap;
}

.mesh-breadcrumbs-list::-webkit-scrollbar {
  display: none;
}

.mesh-breadcrumbs-item {
  display: inline-flex;
  min-width: 0;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.35rem;
}

.mesh-breadcrumbs-separator {
  color: var(--mesh-muted);
  opacity: 0.64;
}

.mesh-breadcrumbs-link,
.mesh-breadcrumbs-button,
.mesh-breadcrumbs-current {
  overflow: hidden;
  max-width: min(24rem, 58vw);
  color: var(--mesh-fg);
  font: inherit;
  font-size: 0.8rem;
  font-weight: 670;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mesh-breadcrumbs-link,
.mesh-breadcrumbs-button {
  color: var(--mesh-muted);
  text-decoration: none;
}

.mesh-breadcrumbs-button {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.mesh-breadcrumbs-link:hover,
.mesh-breadcrumbs-button:hover {
  color: var(--mesh-accent);
  text-decoration: underline;
  text-underline-offset: 0.18em;
}

.mesh-breadcrumbs-button:disabled {
  cursor: default;
  opacity: 0.55;
}

.mesh-breadcrumbs--compact .mesh-breadcrumbs-list {
  gap: 0.3rem;
}

.mesh-breadcrumbs--compact .mesh-breadcrumbs-item {
  gap: 0.3rem;
}

.mesh-breadcrumbs--compact .mesh-breadcrumbs-link,
.mesh-breadcrumbs--compact .mesh-breadcrumbs-button,
.mesh-breadcrumbs--compact .mesh-breadcrumbs-current {
  font-size: 0.76rem;
}

.mesh-app-bar-state {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.28rem;
  padding-left: 0.55rem;
  border-left: 1px solid var(--mesh-border, rgb(255 255 255 / 14%));
  color: var(--mesh-muted);
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
}

.mesh-app-bar-state::before {
  width: 0.42rem;
  height: 0.42rem;
  border-radius: 999px;
  background: var(--mesh-warning, #e6b65f);
  content: "";
}

.mesh-app-bar-state--ready::before {
  background: var(--mesh-success, #65c997);
}

.mesh-app-bar-state--offline::before,
.mesh-app-bar-state--error::before {
  background: var(--mesh-danger, #f08c99);
}

.mesh-app-bar-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.4rem;
  margin-left: auto;
}

.mesh-app-bar-action,
.mesh-invite-trigger {
  display: inline-flex;
  min-height: 2.5rem;
  align-items: center;
  justify-content: center;
  padding: 0.48rem 0.74rem;
  border: 1px solid var(--mesh-border, rgb(255 255 255 / 16%));
  border-radius: var(--mesh-radius-md, 0.75rem);
  background: var(--mesh-surface);
  background: color-mix(in srgb, var(--mesh-surface) 88%, transparent);
  box-shadow: 0 0.65rem 1.7rem rgb(0 0 0 / 19%);
  color: var(--mesh-fg);
  cursor: pointer;
  font: inherit;
  font-size: 0.79rem;
  font-weight: 680;
  line-height: 1;
  text-decoration: none;
  transition:
    background-color 160ms ease,
    border-color 160ms ease,
    transform 160ms ease;
}

.mesh-app-bar-action.mesh-settings-fab {
  position: static;
  width: auto;
  height: auto;
  border-radius: var(--mesh-radius-md, 0.75rem);
  background: var(--mesh-surface);
  background: color-mix(in srgb, var(--mesh-surface) 88%, transparent);
  box-shadow: 0 0.65rem 1.7rem rgb(0 0 0 / 19%);
  backdrop-filter: blur(16px);
  color: var(--mesh-fg);
  font-size: 0.79rem;
  z-index: auto;
}

.mesh-app-bar-invite {
  background: var(--mesh-accent);
  border-color: transparent;
  color: var(--mesh-accent-text, #0c1118);
}

.mesh-app-bar-action:hover,
.mesh-invite-trigger:hover {
  border-color: var(--mesh-accent);
  transform: translateY(-1px);
}

.mesh-app-bar-invite:hover {
  border-color: transparent;
  background: color-mix(in srgb, var(--mesh-accent) 88%, white);
}

/* Legacy controls stay supported while an app is being migrated. Keep Invite
 * clear of the settings cog—the two controls otherwise occupy the same
 * top-right coordinate. */
.mesh-invite-trigger:not(.mesh-app-bar-action) {
  position: absolute;
  top: max(env(safe-area-inset-top), 0.75rem);
  right: max(env(safe-area-inset-right), 0.75rem);
  z-index: 60;
}

.mesh-invite-trigger.mesh-legacy-invite {
  right: calc(max(env(safe-area-inset-right), 0.75rem) + 2.5rem + 0.4rem);
}

@media (max-width: 42rem) {
  .mesh-app-bar-brand {
    max-width: calc(100vw - 11.5rem);
  }

  .mesh-app-bar-eyebrow,
  .mesh-app-bar-state {
    display: none;
  }

  .mesh-app-bar-action,
  .mesh-invite-trigger {
    min-height: 2.75rem;
    padding-inline: 0.72rem;
  }
}

@media (max-width: 25rem) {
  .mesh-app-root:not([data-mesh-breadcrumbs="enabled"]) .mesh-app-bar-brand {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mesh-app-bar-action,
  .mesh-invite-trigger {
    transition: none;
  }
}

/* A profile changes the atmosphere and density of shared components without
 * imposing a second product identity on an app's own feature surface. */
.mesh-app-root[data-mesh-visual-profile="utility"],
.mesh-visual-profile--utility {
  --mesh-ambient: rgb(79 120 198 / 15%);
}

.mesh-app-root[data-mesh-visual-profile="play"],
.mesh-visual-profile--play {
  --mesh-ambient: rgb(245 185 66 / 17%);
}

.mesh-app-root[data-mesh-visual-profile="studio"],
.mesh-visual-profile--studio {
  --mesh-ambient: rgb(177 139 226 / 17%);
}

.mesh-app-root[data-mesh-visual-profile="gather"],
.mesh-visual-profile--gather {
  --mesh-ambient: rgb(89 199 180 / 16%);
}

.mesh-app-root[data-mesh-visual-profile="field"],
.mesh-visual-profile--field {
  --mesh-ambient: rgb(225 183 73 / 16%);
}

.mesh-surface {
  min-width: 0;
  border: 1px solid var(--mesh-border, rgb(255 255 255 / 13%));
  border-radius: var(--mesh-radius-lg, 1.25rem);
  background: var(--mesh-surface);
  color: var(--mesh-fg);
}

.mesh-surface-raised {
  background: var(--mesh-surface-raised, var(--mesh-surface));
  box-shadow: var(--mesh-shadow, 0 18px 44px rgb(0 0 0 / 24%));
}

.mesh-surface-quiet {
  background: color-mix(in srgb, var(--mesh-surface) 60%, transparent);
}

.mesh-surface-accent {
  border-color: color-mix(in srgb, var(--mesh-accent) 42%, var(--mesh-border));
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--mesh-accent) 12%, var(--mesh-surface)),
    var(--mesh-surface)
  );
}

.mesh-surface-padding-none {
  padding: 0;
}

.mesh-surface-padding-sm {
  padding: var(--mesh-space-sm, 0.5rem);
}

.mesh-surface-padding-md {
  padding: var(--mesh-space-md, 1rem);
}

.mesh-surface-padding-lg {
  padding: var(--mesh-space-lg, 1.5rem);
}

.mesh-button {
  display: inline-flex;
  min-height: 2.75rem;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.68rem 0.95rem;
  border: 1px solid transparent;
  border-radius: var(--mesh-radius-md, 0.875rem);
  cursor: pointer;
  font: inherit;
  font-weight: 710;
  line-height: 1.15;
  text-align: center;
  transition:
    background-color 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease,
    transform 160ms ease;
}

.mesh-button:hover:not(:disabled) {
  transform: translateY(-1px);
}

.mesh-button:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.mesh-button-primary {
  background: var(--mesh-accent);
  color: var(--mesh-accent-text, #0c1118);
  box-shadow: 0 0.55rem 1.6rem var(--mesh-ambient, rgb(0 0 0 / 18%));
}

.mesh-button-secondary {
  border-color: var(--mesh-border, rgb(255 255 255 / 16%));
  background: var(--mesh-surface-raised, var(--mesh-surface));
  color: var(--mesh-fg);
}

.mesh-button-quiet {
  background: transparent;
  color: var(--mesh-fg);
}

.mesh-button-danger {
  background: var(--mesh-danger, #d85163);
  color: #fff;
}

.mesh-button-sm {
  min-height: 2.25rem;
  padding: 0.48rem 0.68rem;
  font-size: 0.84rem;
}

.mesh-button-lg {
  min-height: 3.2rem;
  padding: 0.88rem 1.18rem;
  font-size: 1rem;
}

.mesh-button-full-width {
  width: 100%;
}

.mesh-status-pill,
.mesh-presence {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 0.38rem;
  border: 1px solid var(--mesh-border, rgb(255 255 255 / 13%));
  border-radius: 999px;
  background: color-mix(in srgb, var(--mesh-surface) 82%, transparent);
  color: var(--mesh-fg);
  font-size: 0.76rem;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.mesh-status-pill {
  padding: 0.32rem 0.56rem;
}

.mesh-status-pill-md,
.mesh-presence-md {
  padding: 0.45rem 0.7rem;
  font-size: 0.86rem;
}

.mesh-status-pill-dot,
.mesh-presence-indicator {
  width: 0.44rem;
  height: 0.44rem;
  flex: 0 0 auto;
  border-radius: 999px;
  background: var(--mesh-muted);
}

.mesh-status-pill-info .mesh-status-pill-dot {
  background: var(--mesh-accent);
}

.mesh-status-pill-success .mesh-status-pill-dot,
.mesh-status-pill-live .mesh-status-pill-dot,
.mesh-presence-connected .mesh-presence-indicator {
  background: var(--mesh-success, #65c997);
}

.mesh-status-pill-warning .mesh-status-pill-dot,
.mesh-presence-connecting .mesh-presence-indicator {
  background: var(--mesh-warning, #e6b65f);
}

.mesh-status-pill-danger .mesh-status-pill-dot,
.mesh-presence-offline .mesh-presence-indicator {
  background: var(--mesh-danger, #f08c99);
}

.mesh-presence {
  padding: 0.32rem 0.56rem;
  color: var(--mesh-muted);
}

.mesh-presence-count {
  color: var(--mesh-fg);
  font-weight: 750;
}

.mesh-launch {
  display: grid;
  width: min(100%, 44rem);
  min-height: min(38rem, calc(100dvh - 4rem));
  place-items: center;
  margin: 0 auto;
  padding: clamp(4.75rem, 12vw, 7.5rem) clamp(1rem, 5vw, 3rem) 2rem;
  overflow: hidden;
  border-radius: clamp(1.25rem, 4vw, 2.25rem);
  background:
    radial-gradient(
      circle at 82% 17%,
      var(--mesh-ambient, rgb(79 120 198 / 15%)),
      transparent 34%
    ),
    linear-gradient(
      145deg,
      var(--mesh-surface-raised, var(--mesh-surface)),
      var(--mesh-canvas)
    );
  box-shadow: var(--mesh-shadow, 0 18px 44px rgb(0 0 0 / 24%));
  color: var(--mesh-fg);
}

.mesh-launch-content {
  display: grid;
  width: min(100%, 34rem);
  gap: 1.25rem;
}

.mesh-launch-header {
  display: grid;
  gap: 0.7rem;
}

.mesh-launch-eyebrow {
  margin: 0;
  color: var(--mesh-accent);
  font-size: 0.72rem;
  font-weight: 760;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.mesh-launch h1,
.mesh-launch h2,
.mesh-launch h3 {
  max-width: 15ch;
  margin: 0;
  color: var(--mesh-fg);
  font-size: clamp(2.25rem, 7vw, 4.5rem);
  letter-spacing: -0.06em;
  line-height: 0.94;
}

.mesh-launch-promise {
  max-width: 38rem;
  margin: 0;
  color: var(--mesh-muted);
  font-size: clamp(1rem, 2.8vw, 1.18rem);
  line-height: 1.55;
}

.mesh-launch-presence,
.mesh-launch-connection {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  color: var(--mesh-muted);
  font-size: 0.85rem;
}

.mesh-launch-preview {
  min-width: 0;
}

.mesh-launch-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
}

.mesh-launch-action {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 1rem;
  border: 1px solid transparent;
  border-radius: var(--mesh-radius-md, 0.875rem);
  cursor: pointer;
  font: inherit;
  font-weight: 720;
  text-decoration: none;
}

.mesh-launch-action-primary {
  background: var(--mesh-accent);
  color: var(--mesh-accent-text, #0c1118);
}

.mesh-launch-action-secondary {
  border-color: var(--mesh-border, rgb(255 255 255 / 16%));
  background: transparent;
  color: var(--mesh-fg);
}

.mesh-launch-connection-indicator {
  width: 0.48rem;
  height: 0.48rem;
  border-radius: 999px;
  background: var(--mesh-warning, #e6b65f);
}

@media (max-width: 36rem) {
  .mesh-surface-bleed-mobile {
    margin-inline: calc(-1 * var(--mesh-space-md, 1rem));
    border-radius: 0;
  }

  .mesh-launch {
    min-height: calc(100dvh - 1rem);
    padding-top: 5.25rem;
    border-radius: 0;
  }

  .mesh-launch-actions,
  .mesh-launch-action {
    width: 100%;
  }
}

/* Desktop windows can be short even when they are wide. Keep an app's first
 * launch action in the initial viewport instead of treating height as an
 * afterthought. Apps with a deliberately large preview may add their own
 * compact rule, but this is a safe, useful default for every MeshLaunch. */
@media (min-width: 36.0625rem) and (max-height: 44rem) {
  .mesh-launch {
    min-height: 0;
    padding: clamp(1.5rem, 5vh, 2.75rem) clamp(1rem, 5vw, 3rem) 1.25rem;
  }

  .mesh-launch-content {
    gap: 0.85rem;
  }

  .mesh-launch h1,
  .mesh-launch h2,
  .mesh-launch h3 {
    font-size: clamp(2.1rem, 5.4vw, 3.75rem);
  }

  .mesh-launch-promise {
    font-size: 0.96rem;
    line-height: 1.42;
  }

  .mesh-launch-action {
    min-height: 2.75rem;
    padding: 0.65rem 0.9rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mesh-button {
    transition: none;
  }
}

/* SettingsDrawer now composes the Radix-backed MeshSheet. Keep the settings
 * rules scoped to its body so host apps cannot accidentally style the shell. */
.mesh-settings-sheet {
  width: min(420px, 100%);
  margin-left: auto;
  border-radius: 0.8rem 0 0 0.8rem;
}

.mesh-settings-sheet .mesh-sheet-body {
  min-height: 0;
}

.mesh-settings-content {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.mesh-settings-content h3 {
  margin: 0;
  font-size: 0.95rem;
  color: var(--mesh-muted);
}

.mesh-settings-content label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: var(--mesh-muted);
}

.mesh-settings-content label.mesh-settings-check {
  flex-direction: row;
  align-items: center;
  gap: 0.6rem;
  color: var(--mesh-fg);
}

.mesh-settings-content input,
.mesh-settings-content textarea,
.mesh-settings-content select {
  background: #0b0a06;
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  padding: 0.5rem 0.7rem;
  font-size: 0.95rem;
  font-family: inherit;
}

.mesh-settings-content input[type="range"] {
  padding: 0;
}

.mesh-settings-content hr {
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin: 0.5rem 0;
}

.mesh-settings-help {
  font-size: 0.78rem;
  color: var(--mesh-muted);
  margin: 0;
  line-height: 1.4;
}

.mesh-settings-help code {
  background: rgba(255, 255, 255, 0.06);
  padding: 0.05em 0.3em;
  border-radius: 3px;
  font-size: 0.85em;
}

.mesh-settings-error {
  color: #fda4af;
  font-size: 0.78rem;
  line-height: 1.3;
}

.mesh-connection-status {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  padding: 0.35rem 0.55rem;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.2);
  color: var(--mesh-fg);
  font-size: 0.78rem;
  line-height: 1.2;
}

.mesh-connection-status > span[aria-hidden="true"] {
  color: #fbbf24;
  font-size: 0.65rem;
}

.mesh-connection-status-online > span[aria-hidden="true"],
.mesh-connection-status-ready > span[aria-hidden="true"],
.mesh-connection-status-connected > span[aria-hidden="true"] {
  color: #4ade80;
}

.mesh-connection-status-offline > span[aria-hidden="true"] {
  color: #fb7185;
}

.mesh-permission-gate {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.6rem;
  background: rgba(255, 255, 255, 0.04);
}

.mesh-permission-gate p {
  margin: 0;
}
.mesh-live-region {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}
.mesh-qr-dialog {
  display: grid;
  justify-items: center;
  gap: 0.75rem;
}
.mesh-qr-dialog code {
  max-width: 100%;
  overflow-wrap: anywhere;
  font-size: 0.72rem;
}

.mesh-settings-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.mesh-settings-actions button {
  flex: 1;
  padding: 0.7rem;
  font-size: 0.9rem;
  background: var(--mesh-accent);
  color: #0b0a06;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.mesh-settings-actions button + button {
  background: transparent;
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.mesh-settings-footer {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: var(--mesh-muted);
}

.mesh-settings-about-links {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.7rem;
}

.mesh-settings-footer a {
  color: var(--mesh-accent);
  text-decoration: none;
}

/* InviteShareButton also uses MeshSheet so it inherits focus management and
 * Escape/outside-click handling everywhere it is mounted. */
.mesh-invite-sheet {
  width: min(360px, calc(100vw - 2rem));
  border-radius: 0.8rem;
}

.mesh-invite-content {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.mesh-invite-url {
  width: 100%;
  text-align: left;
  color: var(--mesh-fg);
}

@media (max-width: 32rem) {
  .mesh-settings-sheet {
    width: 100%;
    border-radius: 1rem 1rem 0 0;
  }
}

/* Keyboard focus needs to be visible on every app, including app-specific
 * buttons that do not opt into a component class. */
.mesh-app-root :focus-visible {
  outline: 3px solid var(--mesh-accent);
  outline-offset: 3px;
}

/* ------------------------------------------------------------------------- */
/* QRExchange shared widget — pattern used by 20+ viral mesh-* apps.          */
/* ------------------------------------------------------------------------- */

.mesh-qrx {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding: 0.85rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 0.5rem;
}

@media (min-width: 30rem) {
  .mesh-qrx {
    grid-template-columns: auto 1fr;
    align-items: start;
  }
}

.mesh-qrx-section {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-width: 0;
}

.mesh-qrx-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  opacity: 0.6;
}

.mesh-qrx-qr {
  display: flex;
  justify-content: center;
  padding: 0.4rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0.4rem;
}

.mesh-qrx-payload {
  font-size: 0.78rem;
}

.mesh-qrx-payload summary {
  cursor: pointer;
  opacity: 0.55;
}

.mesh-qrx-payload code {
  display: block;
  margin-top: 0.3rem;
  padding: 0.3rem 0.5rem;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 0.3rem;
  font-family: ui-monospace, monospace;
  font-size: 0.72rem;
  word-break: break-all;
}

.mesh-qrx-actions {
  display: flex;
  gap: 0.4rem;
}

.mesh-qrx-btn {
  padding: 0.55rem 1.1rem;
  background: var(--mesh-accent);
  color: #0e1117;
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  font-weight: 600;
}

.mesh-qrx-video {
  width: 100%;
  max-width: 18rem;
  aspect-ratio: 4 / 3;
  background: #000;
  border-radius: 0.4rem;
}

.mesh-qrx-paste {
  display: flex;
  gap: 0.35rem;
}

.mesh-qrx-paste input {
  flex: 1;
  padding: 0.45rem 0.7rem;
  background: rgba(255, 255, 255, 0.05);
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.4rem;
  min-width: 0;
  font-size: 0.85rem;
}

.mesh-qrx-paste button {
  padding: 0.45rem 0.9rem;
  background: transparent;
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 0.4rem;
  cursor: pointer;
  font-weight: 500;
}

.mesh-qrx-paste button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mesh-qrx-error {
  margin: 0;
  padding: 0.4rem 0.6rem;
  background: rgba(248, 113, 113, 0.12);
  border: 1px solid rgba(248, 113, 113, 0.4);
  color: #fda4af;
  border-radius: 0.35rem;
  font-size: 0.82rem;
}

/* Generic viral-app helper classes that any of the 20 apps can reuse. */

.viral-screen {
  max-width: 38rem;
  margin: 0 auto;
  padding: 4.5rem 1.25rem 6rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.viral-screen h1 {
  font-size: 1.6rem;
  margin: 0 0 0.2rem;
  color: var(--mesh-accent);
}

.viral-status {
  margin: 0;
  opacity: 0.7;
  font-size: 0.9rem;
}

.viral-section-title {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  opacity: 0.55;
  margin: 0 0 0.4rem;
}

.viral-name {
  width: 100%;
  padding: 0.6rem 0.9rem;
  background: rgba(255, 255, 255, 0.05);
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.4rem;
}

.viral-empty {
  opacity: 0.5;
  font-style: italic;
  margin: 0.3rem 0;
}

.viral-primary {
  padding: 0.6rem 1.1rem;
  background: var(--mesh-accent);
  color: #0e1117;
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  font-weight: 600;
}

.viral-primary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.viral-ghost {
  padding: 0.5rem 0.95rem;
  background: transparent;
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 0.4rem;
  cursor: pointer;
}

.viral-card {
  padding: 0.8rem 0.95rem;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 0.5rem;
}

.viral-card.is-mine {
  border: 1px solid var(--mesh-accent);
}

.viral-tags {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.viral-tags li {
  padding: 0.22rem 0.55rem;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  font-size: 0.85rem;
}

.viral-tags li.is-me {
  background: rgba(245, 158, 11, 0.2);
  border: 1px solid var(--mesh-accent);
}

/* QRExchange visibility toggle (added 2026-05-17) */
.mesh-qrx {
  position: relative;
}

.mesh-qrx-header {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 1;
}

.mesh-qrx-eye {
  padding: 0.25rem 0.55rem;
  background: rgba(255, 255, 255, 0.08);
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.35rem;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  opacity: 0.7;
  transition: opacity 0.15s;
}

.mesh-qrx-eye:hover {
  opacity: 1;
}

.mesh-qrx.is-collapsed {
  padding: 0.4rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  min-height: 2.2rem;
  align-items: center;
}

.mesh-qrx.is-collapsed .mesh-qrx-header {
  position: static;
}

.mesh-qrx.is-collapsed .mesh-qrx-eye {
  opacity: 0.85;
}

/* Moderator badge — layer-1 security UI */
.mesh-mod {
  padding: 0.65rem 0.85rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.mesh-mod.is-active {
  background: rgba(56, 189, 248, 0.1);
  border-color: rgba(56, 189, 248, 0.35);
}

.mesh-mod.is-me {
  background: rgba(34, 197, 94, 0.14);
  border-color: rgba(34, 197, 94, 0.45);
}

.mesh-mod-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.mesh-mod-icon {
  font-size: 1.25rem;
  line-height: 1;
}

.mesh-mod-text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.mesh-mod-text strong {
  font-size: 0.95rem;
  line-height: 1.2;
}

.mesh-mod-sub {
  font-size: 0.75rem;
  opacity: 0.65;
  margin-top: 0.15rem;
}

.mesh-mod-btn {
  padding: 0.35rem 0.75rem;
  background: transparent;
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.35rem;
  font-size: 0.82rem;
  cursor: pointer;
  font-weight: 500;
}

.mesh-mod-btn:hover {
  border-color: var(--mesh-accent);
}

.mesh-mod-btn.mesh-mod-claim {
  background: var(--mesh-accent);
  color: #0e1117;
  border-color: var(--mesh-accent);
  font-weight: 600;
}

/* ------------------------------------------------------------------------- */
/* <Leaderboard> primitive — drop-in ranked list, used by gratitude, tag,    */
/* tournament, voting, attendance apps.                                       */
/* ------------------------------------------------------------------------- */

.mesh-leaderboard {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.mesh-leaderboard-title {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  opacity: 0.6;
  margin: 0 0 0.15rem;
}

.mesh-leaderboard-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.mesh-leaderboard-row {
  display: grid;
  grid-template-columns: 2rem 1fr auto;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 0.35rem;
}

.mesh-leaderboard-row.is-me {
  border: 1px solid var(--mesh-accent);
  background: rgba(255, 255, 255, 0.06);
}

.mesh-leaderboard-rank {
  opacity: 0.55;
  font-variant-numeric: tabular-nums;
}

.mesh-leaderboard-name {
  font-weight: 500;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mesh-leaderboard-you {
  opacity: 0.55;
  font-weight: 400;
}

.mesh-leaderboard-sub {
  grid-column: 2 / -1;
  font-size: 0.78rem;
  opacity: 0.55;
}

.mesh-leaderboard-score {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--mesh-accent);
}

.mesh-leaderboard-empty {
  opacity: 0.5;
  font-style: italic;
  margin: 0.2rem 0;
}

/* ------------------------------------------------------------------------- */
/* <MeshToasts> primitive — fleet-wide transient announcements                */
/* ------------------------------------------------------------------------- */

.mesh-toasts {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  z-index: 9000;
  pointer-events: none;
  max-width: min(90vw, 26rem);
  width: 100%;
}

.mesh-toasts-top {
  top: 1rem;
}

.mesh-toasts-bottom {
  bottom: 1rem;
}

.mesh-toast {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  padding: 0.55rem 0.9rem;
  background: rgba(10, 14, 22, 0.92);
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-left: 3px solid var(--mesh-accent);
  border-radius: 0.4rem;
  font-size: 0.88rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(6px);
  transition: opacity 0.3s ease;
}

.mesh-toast.is-warning {
  border-left-color: #f59e0b;
}

.mesh-toast.is-error {
  border-left-color: #f87171;
}

.mesh-toast-author {
  font-weight: 600;
  opacity: 0.85;
  white-space: nowrap;
}

.mesh-toast-msg {
  min-width: 0;
}

/* ---- ArmGate (iOS user-gesture button) ---- */
.mesh-arm-gate {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 1.5rem 1rem;
}

.mesh-arm-gate-button {
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: var(--mesh-accent, #f5a524);
  color: #000;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.85rem 1.4rem;
  border-radius: 999px;
  cursor: pointer;
}

.mesh-arm-gate-button:active {
  transform: translateY(1px);
}

.mesh-arm-gate-hint {
  margin: 0;
  font-size: 0.85rem;
  opacity: 0.65;
  max-width: 24rem;
  text-align: center;
}

/* ---- ConfettiLayer (decorative, pointer-events disabled) ---- */
.mesh-confetti-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 9999;
}

.mesh-confetti-piece {
  position: absolute;
  width: 8px;
  height: 14px;
  border-radius: 2px;
  will-change: transform, opacity;
}

/* =========================================================================
   UI primitives (v0.4.0) — production-ready Radix + Sonner-backed components
   with consistent dark-mode styling.
   ========================================================================= */

/* Common button surface used by MeshConfirm + MeshSheet footers */
.mesh-btn {
  appearance: none;
  font: inherit;
  cursor: pointer;
  padding: 0.55rem 1rem;
  border-radius: 0.45rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  color: var(--mesh-fg);
  transition:
    background-color 120ms ease,
    transform 80ms ease;
}
.mesh-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}
.mesh-btn:active:not(:disabled) {
  transform: translateY(1px);
}
.mesh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.mesh-btn-primary {
  background: var(--mesh-accent, #f5a524);
  color: #000;
  font-weight: 600;
  border-color: transparent;
}
.mesh-btn-primary:hover:not(:disabled) {
  filter: brightness(1.1);
  background: var(--mesh-accent, #f5a524);
}
.mesh-btn-danger {
  background: #dc2626;
  color: #fff;
  font-weight: 600;
  border-color: transparent;
}
.mesh-btn-danger:hover:not(:disabled) {
  background: #ef4444;
}
.mesh-btn-ghost {
  background: transparent;
}

/* ---- MeshSheet (Radix Dialog) ---- */
.mesh-sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 50;
  animation: mesh-fade-in 150ms ease;
}
.mesh-sheet {
  position: fixed;
  z-index: 51;
  background: #15161a;
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 1.1rem 1.1rem 1rem;
  animation: mesh-fade-in 180ms ease;
}
.mesh-sheet-centered {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(28rem, calc(100vw - 2rem));
  border-radius: 0.85rem;
}
.mesh-sheet-bottom {
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 1rem 1rem 0 0;
  border-bottom: none;
  max-height: 90vh;
}
.mesh-sheet-header {
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 0.3rem;
}
.mesh-sheet-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
}
.mesh-sheet-description {
  margin: 0;
  font-size: 0.85rem;
  opacity: 0.65;
}
.mesh-sheet-close {
  position: absolute;
  top: -0.2rem;
  right: -0.2rem;
  appearance: none;
  background: transparent;
  border: none;
  color: var(--mesh-fg);
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  padding: 0.3rem 0.5rem;
  opacity: 0.55;
}
.mesh-sheet-close:hover {
  opacity: 1;
}
.mesh-sheet-body {
  overflow-y: auto;
}
.mesh-sheet-footer {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

/* ---- MeshConfirm ---- */
.mesh-confirm-message {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.8;
  line-height: 1.45;
}
.mesh-confirm-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.4rem;
}

/* ---- MeshTabs (Radix Tabs) ---- */
.mesh-tabs {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}
.mesh-tabs-list {
  display: flex;
  gap: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.mesh-tabs-trigger {
  appearance: none;
  background: transparent;
  border: none;
  color: var(--mesh-fg);
  opacity: 0.55;
  padding: 0.55rem 0.9rem;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  font: inherit;
  font-size: 0.9rem;
  margin-bottom: -1px;
}
.mesh-tabs-trigger[data-state="active"] {
  opacity: 1;
  border-bottom-color: var(--mesh-accent);
}
.mesh-tabs-trigger:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}
.mesh-tabs-pill .mesh-tabs-list {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  padding: 0.2rem;
  align-self: center;
}
.mesh-tabs-pill .mesh-tabs-trigger {
  border-radius: 999px;
  margin: 0;
  border-bottom: none;
}
.mesh-tabs-pill .mesh-tabs-trigger[data-state="active"] {
  background: var(--mesh-accent);
  color: #000;
}

/* ---- MeshSlider (Radix Slider) ---- */
.mesh-slider-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  --mesh-slider-accent: var(--mesh-accent);
}
.mesh-slider-header {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  opacity: 0.8;
}
.mesh-slider-value {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}
.mesh-slider {
  position: relative;
  display: flex;
  align-items: center;
  height: 1.2rem;
  user-select: none;
  touch-action: none;
}
.mesh-slider-track {
  position: relative;
  flex-grow: 1;
  height: 0.28rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 999px;
}
.mesh-slider-range {
  position: absolute;
  height: 100%;
  background: var(--mesh-slider-accent);
  border-radius: 999px;
}
.mesh-slider-thumb {
  display: block;
  width: 1.1rem;
  height: 1.1rem;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45);
  cursor: grab;
}
.mesh-slider-thumb:focus-visible {
  outline: 2px solid var(--mesh-slider-accent);
  outline-offset: 2px;
}

/* ---- MeshSegmented (Radix ToggleGroup) ---- */
.mesh-segmented {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}
.mesh-segmented-item {
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: var(--mesh-fg);
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  cursor: pointer;
  font: inherit;
  font-size: 0.85rem;
  transition: background-color 120ms ease;
}
.mesh-segmented-item:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}
.mesh-segmented-item[data-state="on"] {
  background: var(--mesh-accent);
  color: #000;
  border-color: transparent;
  font-weight: 600;
}
.mesh-segmented-item:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.mesh-segmented-sm .mesh-segmented-item {
  padding: 0.25rem 0.6rem;
  font-size: 0.75rem;
}
.mesh-segmented-lg .mesh-segmented-item {
  padding: 0.6rem 1.1rem;
  font-size: 1rem;
}

/* ---- MeshPopover (Radix Popover) ---- */
.mesh-popover {
  background: #15161a;
  color: var(--mesh-fg);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.55rem;
  padding: 0.65rem 0.85rem;
  font-size: 0.9rem;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
  z-index: 52;
  max-width: 22rem;
  animation: mesh-fade-in 120ms ease;
}
.mesh-popover-arrow {
  fill: #15161a;
}

/* ---- MeshSwitch (Radix Switch) ---- */
.mesh-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  cursor: pointer;
  padding: 0.4rem 0;
}
.mesh-switch-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.mesh-switch-label {
  font-size: 0.9rem;
}
.mesh-switch-description {
  font-size: 0.78rem;
  opacity: 0.6;
}
.mesh-switch {
  appearance: none;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  width: 2.3rem;
  height: 1.3rem;
  border-radius: 999px;
  position: relative;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.mesh-switch[data-state="checked"] {
  background: var(--mesh-accent);
}
.mesh-switch-thumb {
  display: block;
  width: 1.05rem;
  height: 1.05rem;
  background: #fff;
  border-radius: 50%;
  transform: translateX(0.125rem);
  transition: transform 140ms cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.35);
}
.mesh-switch[data-state="checked"] .mesh-switch-thumb {
  transform: translateX(1.125rem);
}
.mesh-switch-sm {
  width: 1.85rem;
  height: 1.05rem;
}
.mesh-switch-sm .mesh-switch-thumb {
  width: 0.85rem;
  height: 0.85rem;
}
.mesh-switch-sm[data-state="checked"] .mesh-switch-thumb {
  transform: translateX(0.9rem);
}

/* ---- MeshNameInput ---- */
.mesh-name-input {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  position: relative;
}
.mesh-name-input-label {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  opacity: 0.75;
}
.mesh-name-input-counter {
  font-variant-numeric: tabular-nums;
  font-size: 0.72rem;
  opacity: 0.55;
}
.mesh-name-input-counter-floating {
  position: absolute;
  right: 0.7rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
.mesh-name-input-field {
  appearance: none;
  width: 100%;
  padding: 0.65rem 0.85rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: var(--mesh-fg);
  font: inherit;
  font-size: 0.95rem;
}
.mesh-name-input-field:focus-visible {
  outline: 2px solid var(--mesh-accent);
  outline-offset: 1px;
  border-color: transparent;
}
.mesh-name-input-hint {
  margin: 0;
  font-size: 0.78rem;
  opacity: 0.55;
}

/* ---- MeshEmpty ---- */
.mesh-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.55rem;
  padding: 2rem 1rem;
  opacity: 0.9;
}
.mesh-empty-icon {
  font-size: 2.4rem;
  opacity: 0.7;
}
.mesh-empty-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
}
.mesh-empty-message {
  margin: 0;
  font-size: 0.85rem;
  opacity: 0.65;
  max-width: 28rem;
  line-height: 1.45;
}
.mesh-empty-action {
  margin-top: 0.4rem;
}
.mesh-empty-sm {
  padding: 1rem 0.5rem;
  gap: 0.35rem;
}
.mesh-empty-sm .mesh-empty-icon {
  font-size: 1.5rem;
}
.mesh-empty-lg {
  padding: 3.5rem 1rem;
  gap: 0.75rem;
}
.mesh-empty-lg .mesh-empty-icon {
  font-size: 3.2rem;
}

/* =========================================================================
   UX foundation primitives — semantic, responsive defaults. Apps may layer
   their own visual identity without rebuilding focus, safe-area, or status UI.
   ========================================================================= */

.mesh-app-frame {
  width: min(100%, 76rem);
  min-height: 100%;
  margin-inline: auto;
  padding: max(1rem, env(safe-area-inset-top))
    max(1rem, env(safe-area-inset-right)) max(1rem, env(safe-area-inset-bottom))
    max(1rem, env(safe-area-inset-left));
}
.mesh-app-frame-header,
.mesh-app-frame-actions,
.mesh-app-frame-navigation,
.mesh-app-frame-footer {
  display: flex;
  gap: 0.75rem;
}
.mesh-app-frame-header {
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}
.mesh-app-frame-heading {
  min-width: 0;
}
.mesh-app-frame-heading h1 {
  margin: 0.15rem 0 0;
  color: var(--mesh-fg);
  font-size: clamp(1.65rem, 5vw, 2.6rem);
  letter-spacing: -0.04em;
  line-height: 1.05;
}
.mesh-app-frame-eyebrow {
  color: var(--mesh-accent);
  font-size: 0.76rem;
  font-weight: 750;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.mesh-app-frame-description {
  max-width: 60ch;
  margin-top: 0.5rem;
  color: var(--mesh-muted);
}
.mesh-app-frame-actions {
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.mesh-app-frame-navigation {
  flex-wrap: wrap;
  margin: 0 0 1rem;
}
.mesh-app-frame-breadcrumbs {
  margin: 0 0 0.85rem;
}
.mesh-app-frame-main {
  min-width: 0;
}
.mesh-app-frame-footer {
  margin-top: 1.5rem;
  color: var(--mesh-muted);
  font-size: 0.85rem;
}

.mesh-room-gate,
.mesh-connection-panel,
.mesh-capability-gate,
.mesh-readiness-panel,
.mesh-roster,
.mesh-onboarding {
  border: 1px solid var(--mesh-border, rgba(255, 255, 255, 0.12));
  border-radius: var(--mesh-radius-md, 0.75rem);
  color: var(--mesh-fg);
  background: var(--mesh-surface-raised, var(--mesh-surface));
  box-shadow: var(--mesh-shadow, none);
}
.mesh-room-gate {
  display: grid;
  justify-items: start;
  gap: 0.7rem;
  padding: 1.15rem;
}
.mesh-room-gate h2 {
  margin: 0;
  font-size: 1.1rem;
}
.mesh-room-gate p {
  margin: 0;
  color: var(--mesh-muted);
}
.mesh-room-gate[data-status="error"],
.mesh-room-gate[data-status="stale"] {
  border-color: var(--mesh-warning, #f5b85a);
}
.mesh-room-gate[data-status="offline"] {
  border-color: var(--mesh-danger, #ff8a8a);
}

.mesh-connection-panel {
  padding: 0.75rem 0.9rem;
}
.mesh-connection-panel-summary {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  color: inherit;
  font: inherit;
  text-align: left;
}
.mesh-connection-panel-summary > span:first-child {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-weight: 700;
}
.mesh-connection-panel-summary > span:first-child::before {
  width: 0.58rem;
  height: 0.58rem;
  border-radius: 50%;
  background: var(--mesh-warning, #f5b85a);
  content: "";
}
.mesh-connection-panel-connected
  .mesh-connection-panel-summary
  > span:first-child::before {
  background: var(--mesh-success, #5bd68a);
}
.mesh-connection-panel-offline
  .mesh-connection-panel-summary
  > span:first-child::before,
.mesh-connection-panel-error
  .mesh-connection-panel-summary
  > span:first-child::before {
  background: var(--mesh-danger, #ff8a8a);
}
.mesh-connection-panel details {
  margin-top: 0.75rem;
  border-top: 1px solid var(--mesh-border, rgba(255, 255, 255, 0.12));
  padding-top: 0.75rem;
}
.mesh-connection-panel dl {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.35rem 0.75rem;
  margin: 0 0 0.75rem;
  color: var(--mesh-muted);
  font-size: 0.82rem;
}
.mesh-connection-panel dt {
  font-weight: 700;
}
.mesh-connection-panel dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.mesh-capability-gate {
  padding: 1rem;
}
.mesh-capability-gate h2 {
  margin: 0;
  font-size: 1.15rem;
}
.mesh-capability-gate-description {
  margin-top: 0.4rem;
  color: var(--mesh-muted);
}
.mesh-capability-list,
.mesh-readiness-panel ul,
.mesh-roster-list {
  display: grid;
  gap: 0.55rem;
  margin: 0.9rem 0 0;
  padding: 0;
  list-style: none;
}
.mesh-capability-row,
.mesh-readiness-panel li,
.mesh-roster-peer {
  border: 1px solid var(--mesh-border, rgba(255, 255, 255, 0.12));
  border-radius: var(--mesh-radius-sm, 0.5rem);
  padding: 0.7rem;
  background: color-mix(
    in srgb,
    var(--mesh-surface-raised, var(--mesh-surface)) 86%,
    transparent
  );
}
.mesh-capability-row-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.6rem;
}
.mesh-capability-row-main span {
  color: var(--mesh-muted);
  font-size: 0.78rem;
}
.mesh-capability-description,
.mesh-capability-fallback,
.mesh-capability-test {
  margin-top: 0.5rem;
  color: var(--mesh-muted);
  font-size: 0.86rem;
}
.mesh-capability-row .mesh-btn {
  margin-top: 0.65rem;
}
.mesh-capability-gate-fallback {
  margin-top: 0.85rem;
  border-left: 3px solid var(--mesh-warning, #f5b85a);
  padding-left: 0.7rem;
}

.mesh-readiness-panel {
  padding: 1rem;
}
.mesh-readiness-panel > div:first-child {
  margin-bottom: 0.1rem;
}
.mesh-readiness-panel li {
  display: grid;
  grid-template-columns: minmax(7.5rem, auto) minmax(7rem, 1fr) minmax(0, 1.4fr);
  gap: 0.45rem 0.7rem;
  color: var(--mesh-muted);
  font-size: 0.84rem;
}
.mesh-readiness-panel li strong {
  color: var(--mesh-warning, #f5b85a);
}
.mesh-readiness-panel li[data-ready="true"] strong {
  color: var(--mesh-success, #5bd68a);
}
.mesh-readiness-panel > .mesh-btn {
  margin-top: 0.85rem;
}

.mesh-roster {
  padding: 1rem;
}
.mesh-roster-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.7rem;
}
.mesh-roster-header h2 {
  margin: 0;
  font-size: 1.1rem;
}
.mesh-roster-disclaimer {
  margin: 0.75rem 0 0;
  color: var(--mesh-muted);
  font-size: 0.78rem;
}
.mesh-roster-peer {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}
.mesh-roster-peer-copy {
  display: grid;
  min-width: 0;
  gap: 0.12rem;
}
.mesh-roster-peer-copy strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mesh-roster-peer-state,
.mesh-roster-peer-capabilities {
  color: var(--mesh-muted);
  font-size: 0.76rem;
}
.mesh-roster-peer-stale {
  opacity: 0.65;
}

.mesh-countdown {
  display: inline-grid;
  min-width: 8rem;
  gap: 0.1rem;
  border: 1px solid var(--mesh-border, rgba(255, 255, 255, 0.12));
  border-radius: var(--mesh-radius-sm, 0.5rem);
  padding: 0.55rem 0.7rem;
  background: var(--mesh-surface-raised, var(--mesh-surface));
  font-variant-numeric: tabular-nums;
}
.mesh-countdown-label,
.mesh-countdown-state {
  color: var(--mesh-muted);
  font-size: 0.72rem;
}
.mesh-countdown-value {
  color: var(--mesh-fg);
  font-size: 1.35rem;
  line-height: 1.1;
}
.mesh-cue-banner {
  display: grid;
  gap: 0.65rem;
  border-left: 3px solid var(--mesh-accent);
  border-radius: 0 var(--mesh-radius-sm, 0.5rem) var(--mesh-radius-sm, 0.5rem) 0;
  padding: 0.75rem 0.85rem;
  background: var(--mesh-surface-raised, var(--mesh-surface));
}
.mesh-cue-banner-expired {
  border-left-color: var(--mesh-warning, #f5b85a);
}
.mesh-cue-banner-cancelled {
  border-left-color: var(--mesh-muted);
}
.mesh-cue-banner footer {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.mesh-onboarding {
  width: min(100%, 42rem);
  padding: clamp(1rem, 4vw, 1.5rem);
}
.mesh-onboarding h1 {
  margin: 0;
  font-size: clamp(1.45rem, 5vw, 2rem);
}
.mesh-onboarding nav ol {
  display: flex;
  justify-content: space-between;
  gap: 0.4rem;
  margin: 1rem 0;
  padding: 0;
  list-style: none;
}
.mesh-onboarding nav button {
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--mesh-border, rgba(255, 255, 255, 0.12));
  border-radius: 50%;
  color: var(--mesh-fg);
  background: transparent;
  cursor: pointer;
  font-weight: 700;
}
.mesh-onboarding li[aria-current="step"] button {
  border-color: transparent;
  color: var(--mesh-accent-text, #0e1726);
  background: var(--mesh-accent);
}
.mesh-onboarding > section {
  min-height: 8rem;
  border-top: 1px solid var(--mesh-border, rgba(255, 255, 255, 0.12));
  padding-top: 1rem;
}
.mesh-onboarding > section h2 {
  margin: 0 0 0.4rem;
  font-size: 1.2rem;
}
.mesh-onboarding > section p {
  margin: 0;
  color: var(--mesh-muted);
}
.mesh-onboarding footer {
  display: flex;
  justify-content: space-between;
  gap: 0.7rem;
  margin-top: 1rem;
}
.mesh-onboarding footer button {
  min-height: 2.65rem;
  border: 1px solid var(--mesh-border, rgba(255, 255, 255, 0.12));
  border-radius: var(--mesh-radius-sm, 0.5rem);
  padding: 0.5rem 0.85rem;
  color: var(--mesh-fg);
  background: transparent;
  cursor: pointer;
  font: inherit;
}
.mesh-onboarding footer button:last-child {
  border-color: transparent;
  color: var(--mesh-accent-text, #0e1726);
  background: var(--mesh-accent);
  font-weight: 700;
}

.mesh-async-action {
  display: inline-grid;
  gap: 0.3rem;
}
.mesh-async-action > button {
  min-height: 2.65rem;
  border: 1px solid transparent;
  border-radius: var(--mesh-radius-sm, 0.5rem);
  padding: 0.5rem 0.85rem;
  color: var(--mesh-accent-text, #0e1726);
  background: var(--mesh-accent);
  cursor: pointer;
  font: inherit;
  font-weight: 700;
}
.mesh-async-action-status {
  min-height: 1.1em;
  color: var(--mesh-muted);
  font-size: 0.78rem;
}

.mesh-form-error-summary {
  margin-bottom: 1rem;
  border-left: 3px solid var(--mesh-danger, #ff8a8a);
  padding: 0.65rem 0.8rem;
  color: var(--mesh-fg);
  background: color-mix(in srgb, var(--mesh-danger, #ff8a8a) 12%, transparent);
}
.mesh-form-error-summary ul {
  margin: 0.35rem 0 0;
  padding-left: 1.15rem;
}
.mesh-field {
  display: grid;
  gap: 0.35rem;
}
.mesh-field-label {
  color: var(--mesh-fg);
  font-size: 0.86rem;
  font-weight: 700;
}
.mesh-field-hint {
  color: var(--mesh-muted);
  font-size: 0.78rem;
}
.mesh-field-error {
  color: var(--mesh-danger, #ff8a8a);
  font-size: 0.8rem;
}
.mesh-field input,
.mesh-field select,
.mesh-field textarea,
.mesh-listbox-search {
  width: 100%;
  border: 1px solid var(--mesh-border, rgba(255, 255, 255, 0.16));
  border-radius: var(--mesh-radius-sm, 0.5rem);
  padding: 0.62rem 0.72rem;
  color: var(--mesh-fg);
  background: color-mix(
    in srgb,
    var(--mesh-surface-raised, var(--mesh-surface)) 78%,
    transparent
  );
  font: inherit;
}
.mesh-field textarea {
  min-height: 6rem;
  resize: vertical;
}
.mesh-field--invalid input,
.mesh-field--invalid select,
.mesh-field--invalid textarea {
  border-color: var(--mesh-danger, #ff8a8a);
}

.mesh-listbox {
  display: grid;
  gap: 0.5rem;
}
.mesh-listbox-label {
  color: var(--mesh-fg);
  font-size: 0.86rem;
  font-weight: 700;
}
.mesh-listbox-options {
  display: grid;
  gap: 0.35rem;
  max-height: min(20rem, 45vh);
  overflow: auto;
  border: 1px solid var(--mesh-border, rgba(255, 255, 255, 0.12));
  border-radius: var(--mesh-radius-sm, 0.5rem);
  padding: 0.35rem;
  background: var(--mesh-surface-raised, var(--mesh-surface));
}
.mesh-listbox-option {
  display: grid;
  gap: 0.15rem;
  width: 100%;
  border: 1px solid transparent;
  border-radius: calc(var(--mesh-radius-sm, 0.5rem) - 0.15rem);
  padding: 0.6rem 0.65rem;
  color: var(--mesh-fg);
  background: transparent;
  cursor: pointer;
  font: inherit;
  text-align: left;
}
.mesh-listbox-option:hover,
.mesh-listbox-option.is-active {
  border-color: color-mix(in srgb, var(--mesh-accent) 60%, transparent);
  background: color-mix(in srgb, var(--mesh-accent) 12%, transparent);
}
.mesh-listbox-option.is-selected {
  border-color: var(--mesh-accent);
}
.mesh-listbox-option-description,
.mesh-listbox-empty {
  margin: 0;
  color: var(--mesh-muted);
  font-size: 0.8rem;
}

.mesh-page :focus-visible,
.mesh-theme :focus-visible {
  outline: 3px solid var(--mesh-focus-ring, var(--mesh-accent));
  outline-offset: 2px;
}

@media (max-width: 640px) {
  .mesh-app-frame-header {
    flex-direction: column;
  }
  .mesh-app-frame-actions {
    justify-content: flex-start;
  }
  .mesh-readiness-panel li {
    grid-template-columns: 1fr;
    gap: 0.15rem;
  }
  .mesh-connection-panel dl {
    grid-template-columns: 1fr;
    gap: 0.1rem;
  }
  .mesh-connection-panel dd {
    margin-bottom: 0.45rem;
  }
}

/* ---- MeshProgressBar ---- */
.mesh-progressbar {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  --mesh-progress-accent: var(--mesh-accent);
}
.mesh-progressbar-header {
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
  opacity: 0.85;
}
.mesh-progressbar-right {
  font-variant-numeric: tabular-nums;
}
.mesh-progressbar-track {
  width: 100%;
  height: 0.4rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  overflow: hidden;
}
.mesh-progressbar-sm .mesh-progressbar-track {
  height: 0.25rem;
}
.mesh-progressbar-lg .mesh-progressbar-track {
  height: 0.65rem;
}
.mesh-progressbar-fill {
  height: 100%;
  background: var(--mesh-progress-accent);
  border-radius: 999px;
  transition: width 180ms cubic-bezier(0.4, 0, 0.2, 1);
}
.mesh-progressbar.is-indeterminate .mesh-progressbar-fill {
  width: 35% !important;
  animation: mesh-progress-indeterminate 1.2s ease-in-out infinite;
}
@keyframes mesh-progress-indeterminate {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(300%);
  }
}

/* ---- MeshDatePicker (Radix Popover + react-day-picker) ---- */
.mesh-datepicker {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.mesh-datepicker-label {
  font-size: 0.8rem;
  opacity: 0.75;
}
.mesh-datepicker-trigger {
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.85rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: var(--mesh-fg);
  font: inherit;
  font-size: 0.95rem;
  cursor: pointer;
  text-align: left;
  min-width: 12rem;
  gap: 0.5rem;
}
.mesh-datepicker-trigger:not(.has-value) {
  color: rgba(255, 255, 255, 0.5);
}
.mesh-datepicker-icon {
  opacity: 0.65;
}
.mesh-datepicker-popover {
  padding: 0.4rem;
}
.rdp-root {
  --rdp-accent-color: var(--mesh-accent);
  --rdp-background-color: rgba(245, 165, 36, 0.18);
  --rdp-day_button-height: 2rem;
  --rdp-day_button-width: 2rem;
  font-size: 0.85rem;
}

/* ---- Sonner toast theme override ---- */
[data-sonner-toaster] {
  --normal-bg: #15161a;
  --normal-border: rgba(255, 255, 255, 0.1);
  --normal-text: var(--mesh-fg);
}

@keyframes mesh-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
